"use client";

/**
 * Main nav, which allows switching between global contexts (eg spaces, user settings).
 */

import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { usePathname } from "next/navigation";
import {
  buildURL,
  Box,
  Icon,
  utils,
  Flex,
  Tooltip,
  openIntercom,
  SpacePermission,
  LinkWrapper,
} from "@thenounproject/lingo-core";

import { useSelectSpace } from "@selectors/entities/spaces";

import SpacePicker from "./SpacePicker";
import LoginBadge from "./LoginBadge";

import { NavPointTypes } from "@redux/navPoint/NavPoint";
import useShowModal, { ModalTypes } from "@redux/actions/useModals";
import useCurrentUser from "@actions/users/useCurrentUser";
import useNavPoint from "@hooks/useNavPoint";
import { getColor } from "../../../../core/src/components/utils";

const KIT_MENU_SOURCE = "KIT_MENU_SOURCE";

type NavItem = {
  label: string;
  iconId: string;
  path?: string;
  onClick?: () => void;
  hidden?: boolean;
  source?: string;
  showNotificationBadge?: boolean;
};

const SuperNavInner = styled(Flex).attrs({
  pt: "m",
  ps: "s",
  height: "100%",
  flexDirection: "column",
  flexGrow: 1,
  justifyContent: "space-between",
  alignItems: "center",
  overflowY: "auto",
  overflowX: "hidden",
})`
  scrollbar-width: thin;
  scrollbar-color: ${getColor("grayDarkest")} transparent;
  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: ${getColor("grayDarkest")};
    border-radius: 2px;
  }
`;

const NavItemsInner = styled(Flex).attrs({
  flexDirection: "column",
  justifyContent: "space-between",
  alignItems: "center",
  gap: "s",
})`
  a,
  button {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
  }
  a,
  button {
    span {
      transition: opacity 0.3s ease;
      opacity: 0;
      color: white !important;
    }
  }
  a[aria-current="page"],
  a:hover,
  button:hover {
    span {
      opacity: 1;
    }
  }
`;

type NavItemWrapperProps = {
  isActive: boolean;
};
const NavItemWrapper = styled(Flex).attrs<NavItemWrapperProps>(props => ({
  background: props.isActive ? "grayDarkest" : "black",
  position: "relative",
  justifyContent: "center",
  alignItems: "center",
  flexDirection: "column",
}))<NavItemWrapperProps>`
  border-radius: 8px;
  width: 48px;
  height: 48px;
  transition: background-color 0.2s ease;
  &:hover {
    background-color: ${props => (props.isActive ? null : utils.getColor("navbarHoverBackground"))};
  }
`;

const SuperNav: React.FC = () => {
  const navPoint = useNavPoint();
  const { user } = useCurrentUser();
  const space = useSelectSpace();
  const [unreadChatCount, setUnreadChatCount] = useState(0);
  const pathname = usePathname();
  const { showModal } = useShowModal();

  /**
   * Hook into the Intercom chat widget to update the unread chat count.
   * Run on a timeout to try to ensure the script has loaded.
   */
  useEffect(() => {
    const checkIntercomUnread = setTimeout(() => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      window.Intercom?.("onUnreadCountChange", (count: number) => {
        setUnreadChatCount(count);
      });
    }, 2000);
    return () => clearTimeout(checkIntercomUnread);
  }, []);

  const openSearchModal = () => showModal(ModalTypes.SEARCH_MODAL, {});

  const { access: { permissions = [] } = {} } = space || {},
    canViewDashboard = permissions.includes(SpacePermission.viewDashboard),
    canViewMembers = permissions.includes(SpacePermission.viewMembers),
    canSearch = permissions.includes(SpacePermission.searchAssets),
    canViewLibrary = permissions.includes(SpacePermission.viewAssetLibrary),
    canViewProducts = permissions.includes(SpacePermission.manageProducts),
    canViewAnalytics = permissions.includes(SpacePermission.viewAnalytics),
    canAccessSettings =
      permissions.includes(SpacePermission.edit) ||
      permissions.includes(SpacePermission.viewAnalytics) ||
      permissions.includes(SpacePermission.manageDownloadRequirements),
    canManageBilling = permissions.includes(SpacePermission.manageBilling);

  const settingsNavItems: NavItem[] = [
    { label: "Profile", path: "/settings/profile/", iconId: "navigation.user" },
    { label: "Password", path: "/settings/password/", iconId: "info.lock-closed" },
    { label: "Comm.", path: "/settings/email/", iconId: "navigation.mail" },
    { label: "Privacy", path: "/settings/privacy/", iconId: "navigation.privacy" },
  ];

  const spaceNavItems: NavItem[] = [
    {
      label: "Dashboard",
      path: buildURL("/dashboard", { space }),
      iconId: "navigation.kit-menu",
      source: KIT_MENU_SOURCE,
      hidden: !canViewDashboard,
    },
    {
      label: "Search",
      onClick: openSearchModal,
      iconId: "navigation.search",
      hidden: !canSearch,
    },
    {
      label: "Library",
      path: buildURL("/library", { space }),
      iconId: "marketing.storage",
      hidden: !canViewLibrary,
    },
    {
      label: "Products",
      path: buildURL("/products", { space }),
      iconId: "content.bag",
      hidden: !canViewProducts,
    },
    {
      label: "Users",
      path: buildURL("/members", { space }),
      iconId: "navigation.user-group",
      hidden: !canViewMembers,
    },
    {
      label: "Insights",
      path: buildURL("/insights", { space }),
      iconId: "navigation.chart",
      hidden: !canViewAnalytics,
    },
    {
      label: "Settings",
      path: buildURL("/settings", { space }),
      iconId: "navigation.settings",
      hidden: !canAccessSettings,
    },
    {
      label: "Billing",
      path: buildURL("/billing", { space }),
      iconId: "navigation.credit-card",
      hidden: !canManageBilling,
    },
  ];

  const navItems = navPoint?.type === NavPointTypes.UserSettings ? settingsNavItems : spaceNavItems;

  const renderNavItems = (items: NavItem[]) => {
    return items.map((item: NavItem) => {
      const checkIsActive = (itemPath: string, locationPathName: string) => {
        const endOfItemPath = itemPath?.endsWith("/")
          ? itemPath?.slice(0, -1)
          : itemPath?.split("/").pop();
        return locationPathName.includes(endOfItemPath);
      };
      if (item.hidden) return null;
      const inner = (
        <NavItemWrapper
          data-tooltip-source={item.label}
          data-popup-source={item?.source}
          isActive={checkIsActive(item?.path, pathname)}>
          {item.showNotificationBadge && (
            <Box
              position="absolute"
              top={0}
              right={0}
              background="red"
              height={10}
              width={10}
              borderRadius="100%"
            />
          )}
          <Icon iconId={item.iconId} fill="white" />
        </NavItemWrapper>
      );
      const tooltip = (
        <Tooltip
          source={item.label}
          direction={Tooltip.Direction.Right}
          ml="12"
          transitionDelay="0.5s"
          interactive={false}
          zIndex={5000}>
          {item.label}
        </Tooltip>
      );
      if (item.path)
        return (
          <span key={item.label}>
            <LinkWrapper link={item.path}>{inner}</LinkWrapper>
            {tooltip}
          </span>
        );
      return (
        <span key={item.label}>
          <button data-testid={item.label} onClick={item.onClick}>
            {inner}
          </button>
          {tooltip}
        </span>
      );
    });
  };

  return (
    <SuperNavInner>
      <Flex flex="1" flexDirection="column" textAlign="center">
        <div>
          <Icon iconId="logo.mark" fill="white" size={40} />
        </div>
        <Box maxHeight="60px" flex="1" />
        <NavItemsInner>{renderNavItems(navItems)}</NavItemsInner>
      </Flex>
      <Box mb="l">
        <NavItemsInner>
          {renderNavItems([
            {
              label: "Help",
              onClick: () => openIntercom(),
              iconId: "info.help",
              showNotificationBadge: unreadChatCount > 0,
            },
          ])}
        </NavItemsInner>
        {user?.id ? (
          <Flex justifyContent="center" alignItems="center">
            <SpacePicker />
          </Flex>
        ) : (
          <LoginBadge />
        )}
      </Box>
    </SuperNavInner>
  );
};

export default SuperNav;
